/**
 * Pure helpers for the Reports hub's Audit & Timesheet Reports sub-view.
 *
 * Split out of ReportsHubPage.tsx so the derived logic can be tested directly —
 * the same arrangement as vistaExportHelpers.ts. Everything here is a pure
 * function of the period payload; nothing touches React or the DOM.
 */
import { Timesheet } from '../../interfaces/AuditInterface';

/** Hash a name to a stable hue — the house avatar treatment. */
export function hueOf(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

export function initialsOf(first: string, last: string): string {
  const f = (first || '').trim()[0] ?? '';
  const l = (last || '').replace(/[^A-Za-z]/g, '')[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

/** "Last, First" — the list and identity-header format. */
export function displayName(e: { firstName: string; lastName: string }): string {
  return `${e.lastName}, ${e.firstName}`.trim();
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Parse the YYYY-MM-DD prefix only. These are SQL `date` values with no
 * meaningful time component — going through the Date constructor and reading
 * local getters shifts them a day in negative-offset zones, which is exactly
 * where this app runs.
 */
export function ymd(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
  if (!match) return null;
  const m = +match[2];
  const d = +match[3];
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y: +match[1], m, d };
}

/**
 * "Aug 10 – 16, 2026", or "Aug 27 – Sep 2, 2026" across a month boundary.
 *
 * Always rendered from the payload's own startDate/endDate and NEVER computed
 * from the week number: PayPeriod rows are hand-maintained data with no
 * constraint tying week numbers to dates, and NPM's calendar is not in this
 * repo. See handoff C1.
 */
export function formatRange(startISO: string, endISO: string): string {
  const s = ymd(startISO);
  const e = ymd(endISO);
  if (!s || !e) return '';
  const left = `${MONTHS[s.m - 1]} ${s.d}`;
  const right = s.m === e.m ? `${e.d}` : `${MONTHS[e.m - 1]} ${e.d}`;
  return `${left} – ${right}, ${e.y}`;
}

/**
 * Group key for one period's accounting year.
 *
 * Typed `number` on the interface, but the column is a NULLABLE char(4) and the
 * API hands it through untouched — so null and "" are both reachable at runtime
 * whatever the type says. Widen deliberately rather than trust it, and keep
 * unknowns in their own bucket instead of silently dropping the rows.
 */
export function yearKey(p: Timesheet): string {
  const raw: unknown = p.payPeriod.accountingYear;
  return raw === null || raw === undefined || raw === '' ? 'unknown' : String(raw);
}

export interface YearGroup {
  key: string;
  label: string;
  periods: Timesheet[];
}

/** Newest accounting year first; the unknown bucket always sorts last. */
export function groupByYear(periods: Timesheet[]): YearGroup[] {
  const map = new Map<string, Timesheet[]>();
  periods.forEach((p) => {
    const k = yearKey(p);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(p);
  });
  return [...map.entries()]
    .map(([key, ps]) => ({ key, label: key === 'unknown' ? 'Year not set' : key, periods: ps }))
    .sort((a, b) => {
      if (a.key === 'unknown') return 1;
      if (b.key === 'unknown') return -1;
      return Number(b.key) - Number(a.key);
    });
}

/** Widest run of missing weeks worth naming. Beyond this the list stops being
 *  readable and almost certainly means the data isn't what we assume. */
const MAX_GAP_RUN = 60;

/**
 * Week numbers missing between two adjacent present rows inside one year group.
 *
 * Reports the hole and deliberately NOT a cause. A `Timesheet` row is only ever
 * created on submit (one INSERT in the whole API), so a gap is far more often
 * "never submitted", leave, layoff, seasonal, pre-hire or post-termination than
 * it is an unlock. See handoff C1 — the UI copy must stay causally neutral.
 *
 * Returns [] for the unknown-year bucket and whenever the numbers don't descend
 * cleanly: PayPeriod has no uniqueness constraint on (AccountingYear,
 * WeekNumber), so duplicates and non-monotonic runs are representable and must
 * not produce nonsense.
 */
export function missingWeeksBetween(
  prev: Timesheet | undefined,
  curr: Timesheet,
  yearGroupKey: string,
): number[] {
  if (!prev || yearGroupKey === 'unknown') return [];
  const a = prev.payPeriod.weekNumber;
  const b = curr.payPeriod.weekNumber;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return [];
  const span = a - b;
  if (span <= 1) return [];            // adjacent, duplicate, or out of order
  if (span > MAX_GAP_RUN) return [];   // nonsense run — say nothing rather than list 200 weeks
  const out: number[] = [];
  for (let w = a - 1; w > b; w--) out.push(w);
  return out;
}
